import type { Match, MatchCompetition, TournamentOutcome } from "@/lib/types";

/**
 * Competition classification helpers (migration 0052, issue #666).
 *
 * **The rule this module exists to hold: only `league` counts toward
 * the season record.** Tournament and friendly fixtures are excluded
 * from W-D-L, points, goals for/against, goal difference, and clean
 * sheets — while still counting as matches played and replays
 * available, because the video is real and coaches need to find it.
 *
 * **The two-context rule.** A match decided on penalties is a DRAW in
 * the season record (league convention: a shootout doesn't change the
 * scoreline) but a WIN in a tournament record (knockout convention: the
 * shootout decides who advances). The same row therefore yields
 * different results in the two panels. That is deliberate, not a bug —
 * see `countsTowardRecord` and `computeTournamentRecord` below.
 *
 * Keeping both rules here rather than inline at each call site is the
 * point: the season record, the tournament sections, and four score
 * displays all depend on them, and a divergence would be invisible
 * until someone noticed the standings were wrong.
 */

/** Label shown on a match card/row badge. Null for league matches —
 * the default needs no badge. */
export function competitionLabel(competition: MatchCompetition): string | null {
  switch (competition) {
    case "tournament":
      return "Tournament";
    case "friendly":
      return "Friendly";
    default:
      return null;
  }
}

/** Human label for a tournament outcome, e.g. `"Champions"`. */
export function outcomeLabel(outcome: TournamentOutcome): string {
  switch (outcome) {
    case "champions":
      return "Champions";
    case "finalist":
      return "Finalist";
    case "semi_finalist":
      return "Semi-finalist";
    case "third_place":
      return "Third place";
    case "group_stage":
      return "Group stage";
  }
}

/**
 * Does this match feed the SEASON record?
 *
 * Only league matches do. This is the single gate the season stats
 * loop consults; everything else about a tournament match (its score,
 * its shootout, its video) is still real data.
 */
export function countsTowardRecord(match: Pick<Match, "competition">): boolean {
  // A row that predates 0052 has no competition value; treat it as
  // league, which is exactly how it behaved before the column existed.
  return (match.competition ?? "league") === "league";
}

/** True when this match belongs to a tournament. */
export function isTournament(match: Pick<Match, "competition">): boolean {
  return match.competition === "tournament";
}

/**
 * The section key a tournament match groups under.
 *
 * Case-insensitive and trimmed, mirroring how `seasons` dedupes names
 * (migration 0007): "Fall Classic 2026", "fall classic 2026", and
 * " Fall Classic 2026 " are one tournament, not three sections with
 * three separate records.
 *
 * Returns `""` for an unnamed tournament match — a legitimate state,
 * since a coach uploading video mid-tournament shouldn't be blocked on
 * naming it. Those group under a generic heading.
 */
export function tournamentKey(match: Pick<Match, "tournament_name">): string {
  return (match.tournament_name ?? "").trim().toLowerCase();
}

export type TournamentRecord = {
  wins: number;
  draws: number;
  losses: number;
  goalsFor: number;
  goalsAgainst: number;
};

/**
 * Compute a tournament's record from its matches.
 *
 * **A shootout win counts as a WIN here** — the opposite of the season
 * record, and the reason this is a separate function rather than a
 * parameter on the season one. In knockout play the shootout is how the
 * tie is resolved, so reporting it as a draw would show a team that won
 * every knockout on penalties as having drawn its way to the title.
 *
 * PK goals still never touch goalsFor/goalsAgainst — those stay the
 * regulation scoreline in both contexts.
 *
 * `sideOf` resolves which side the viewing team played on, supplied by
 * the caller so this matches the rest of that surface's home/away
 * resolution.
 */
export function computeTournamentRecord(
  matches: Match[],
  sideOf: (match: Match) => "home" | "away" | "none",
): TournamentRecord {
  const out: TournamentRecord = {
    wins: 0,
    draws: 0,
    losses: 0,
    goalsFor: 0,
    goalsAgainst: 0,
  };
  for (const m of matches) {
    const side = sideOf(m);
    if (side === "none") continue;
    if (
      m.match_status !== "completed" ||
      m.score_home == null ||
      m.score_away == null
    )
      continue;
    const isHome = side === "home";
    const ours = isHome ? m.score_home : m.score_away;
    const theirs = isHome ? m.score_away : m.score_home;
    out.goalsFor += ours;
    out.goalsAgainst += theirs;
    if (ours > theirs) {
      out.wins += 1;
    } else if (ours < theirs) {
      out.losses += 1;
    } else {
      // Level after regulation. In a knockout the shootout resolves it,
      // so it lands in wins/losses here rather than draws — unlike the
      // season record, where the same row stays a draw.
      const pkHome = m.pk_home;
      const pkAway = m.pk_away;
      if (pkHome != null && pkAway != null && pkHome !== pkAway) {
        const oursPk = isHome ? pkHome : pkAway;
        const theirsPk = isHome ? pkAway : pkHome;
        if (oursPk > theirsPk) out.wins += 1;
        else out.losses += 1;
      } else {
        out.draws += 1;
      }
    }
  }
  return out;
}

export type TournamentGroup = {
  /** Normalised grouping key; `""` for unnamed. */
  key: string;
  /** Display name — the first non-empty spelling encountered, so the
   * heading shows the coach's own capitalisation. */
  name: string | null;
  matches: Match[];
  /** Outcome for the group, resolved from the LATEST-DATED match that
   * carries one — the final is the authoritative word on how the team
   * finished, and a coach typically sets it there.
   *
   * Resolved by comparing dates rather than by iteration order: the
   * team page hands us a date-DESCENDING list, so "last one wins" would
   * pick the OLDEST match and report a group-stage exit for a team that
   * went on to win the final. Comparing dates makes this independent of
   * however the caller happened to sort. */
  outcome: TournamentOutcome | null;
};

/**
 * Split a match list into league/friendly matches and tournament groups.
 *
 * Input order is preserved within each group, so a caller that already
 * sorted by date newest-first keeps that ordering.
 */
export function groupByTournament(matches: Match[]): {
  seasonMatches: Match[];
  tournaments: TournamentGroup[];
} {
  const seasonMatches: Match[] = [];
  const byKey = new Map<string, TournamentGroup>();
  // Date of the match each group's current outcome came from, so a
  // later-dated one can supersede it regardless of iteration order.
  const outcomeDate = new Map<string, string>();

  for (const m of matches) {
    if (!isTournament(m)) {
      seasonMatches.push(m);
      continue;
    }
    const key = tournamentKey(m);
    let group = byKey.get(key);
    if (!group) {
      group = { key, name: null, matches: [], outcome: null };
      byKey.set(key, group);
    }
    group.matches.push(m);
    const spelling = (m.tournament_name ?? "").trim();
    if (!group.name && spelling) group.name = spelling;
    // Latest-dated match wins — NOT last-iterated. See
    // TournamentGroup.outcome: the caller sorts newest-first, so
    // iteration order would hand us the oldest match.
    if (m.tournament_outcome) {
      const date = m.date ?? "";
      if (group.outcome === null || date >= outcomeDate.get(key)!) {
        group.outcome = m.tournament_outcome;
        outcomeDate.set(key, date);
      }
    }
  }

  return { seasonMatches, tournaments: [...byKey.values()] };
}

/**
 * Distinct tournament names present in a match list, for the edit
 * modal's autocomplete. Sorted alphabetically; dedupes case-insensitively
 * (keeping the first spelling seen) so "Fall Classic" and "fall classic"
 * offer one suggestion rather than two.
 */
export function knownTournamentNames(matches: Match[]): string[] {
  const seen = new Map<string, string>();
  for (const m of matches) {
    const name = (m.tournament_name ?? "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (!seen.has(key)) seen.set(key, name);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}
